import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged, 
  User,
  AuthError
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { apiService } from '../services/api';

interface UserRole {
  role: 'admin' | 'customer';
  customerId?: string;
  permissions?: string[];
}

interface AuthContextType {
  currentUser: User | null;
  username: string | null;
  userRole: UserRole | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  isAdmin: boolean;
  isCustomer: boolean;
  getUserRole: () => Promise<UserRole | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);

  const getUserRole = async (): Promise<UserRole | null> => {
    if (!currentUser) return null;
    
    try {
      // Get fresh ID token with custom claims
      const token = await currentUser.getIdTokenResult(true);
      const claims = token.claims;
      
      // Check if user has admin role from custom claims
      if (claims?.role === 'admin') {
        return { 
          role: 'admin',
          permissions: Array.isArray(claims?.permissions) ? claims.permissions : ['manage_customers', 'view_analytics', 'configure_widgets']
        };
      }
      
      // Check if user has customer role from custom claims
      if (claims?.role === 'customer') {
        return { 
          role: 'customer',
          customerId: typeof claims?.customerId === 'string' ? claims.customerId : undefined,
          permissions: Array.isArray(claims?.permissions) ? claims.permissions : ['use_widget']
        };
      }
      
      // For new Firebase users without custom claims, try auto-creation
      if (!claims?.role) {
        try {
          console.log('New Firebase user detected, checking for existing data...');
          
          // First check if there's localStorage data that needs syncing
          const localData = localStorage.getItem('customerData');
          if (localData) {
            const parsedData = JSON.parse(localData);
            
            // If localStorage data needs syncing, try to create customer record
            if (parsedData.needsSync && parsedData.userId === currentUser.uid) {
              console.log('Found localStorage data that needs syncing...');
              try {
                const customerData = await apiService.autoCreateCustomerForFirebaseUser({
                  firebase_uid: currentUser.uid,
                  email: currentUser.email!,
                  display_name: parsedData.name || currentUser.displayName || undefined
                });
                
                console.log('✅ Synced localStorage data to database:', customerData);
                
                // Update localStorage to remove needsSync flag
                const updatedLocalData = {
                  ...parsedData,
                  customerId: customerData.customer_id,
                  apiKey: customerData.api_key,
                  needsSync: false
                };
                localStorage.setItem('customerData', JSON.stringify(updatedLocalData));
                
                return { 
                  role: 'customer',
                  customerId: customerData.customer_id,
                  permissions: ['use_widget']
                };
              } catch (syncError) {
                console.error('Failed to sync localStorage data:', syncError);
                // Continue with normal auto-creation below
              }
            }
          }
          
          // Normal auto-creation for new users
          console.log('Creating new customer record...');
          const customerData = await apiService.autoCreateCustomerForFirebaseUser({
            firebase_uid: currentUser.uid,
            email: currentUser.email!,
            display_name: currentUser.displayName || undefined
          });
          
          console.log('✅ Customer record created:', customerData);
          
          // Return customer role while Firebase claims are updated
          return { 
            role: 'customer',
            customerId: customerData.customer_id,
            permissions: ['use_widget']
          };
          
        } catch (error) {
          console.error('Failed to create customer record:', error);
          // Default to customer role even if auto-creation fails
          return { role: 'customer', permissions: ['use_widget'] };
        }
      }
      
      // Fallback to customer role
      return { role: 'customer', permissions: ['use_widget'] };
      
    } catch (error) {
      console.error('Error getting user role:', error);
      return { role: 'customer', permissions: ['use_widget'] };
    }
  };

  const login = async (email: string, password: string): Promise<void> => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      const authError = error as AuthError;
      console.error('Login error:', authError.message);
      throw new Error('Invalid email or password');
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await signOut(auth);
      setUserRole(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw new Error('Failed to logout');
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          const role = await getUserRole();
          setUserRole(role);
        } catch (error) {
          console.error('Error setting user role:', error);
          // Set default customer role on error
          setUserRole({ role: 'customer', permissions: ['use_widget'] });
        }
      } else {
        setUserRole(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [currentUser]);

  const value: AuthContextType = {
    currentUser,
    username: currentUser?.email || null,
    userRole,
    login,
    logout,
    loading,
    isAdmin: userRole?.role === 'admin',
    isCustomer: userRole?.role === 'customer',
    getUserRole
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 